import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import AdminActionPasswordModal from '../components/AdminActionPasswordModal';
import './AdminDashboard.css';

type ActiveTab = 'users' | 'businesses' | 'requests' | 'notifications';

// ── Pending action state (what is waiting for password confirmation) ─────────
type PendingAction =
  | { type: 'deleteUser';    userId: string }
  | { type: 'deleteBusiness'; businessId: string }
  | { type: 'sendCode';      requestId: string }
  | { type: 'deleteRequest'; requestId: string }
  | { type: 'clearRequests'; deleteAll: boolean }
  | { type: 'sendNotification'; title: string; message: string; targetBusinessId?: string; targetRole: string };

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending:    { label: 'Chờ duyệt',  color: '#f59e0b' },
  code_sent:  { label: 'Đã gửi mã', color: '#3b82f6' },
  verified:   { label: 'Đã xác nhận', color: '#10b981' },
  rejected:   { label: 'Từ chối',   color: '#ef4444' },
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('users');

  const [users, setUsers]         = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [requests, setRequests]   = useState<any[]>([]);

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [noBizFilter, setNoBizFilter] = useState(false);


  // Notification form
  const [notifyTitle, setNotifyTitle]           = useState('');
  const [notifyMessage, setNotifyMessage]       = useState('');
  const [targetBusinessId, setTargetBusinessId] = useState('');
  const [targetRole, setTargetRole]             = useState('all');

  // Subscription modal
  const [subModalOpen, setSubModalOpen]       = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [newExpiryDate, setNewExpiryDate]     = useState('');

  // Password reset modal
  const [resetModalOpen, setResetModalOpen]   = useState(false);
  const [selectedUser, setSelectedUser]       = useState<any>(null);
  const [newPassword, setNewPassword]         = useState('');

  // 🔐 Admin action password modal
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getAdminUsers();
      setUsers(res.data || []);
    } catch (err: any) { setError(err.message || 'Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getAdminBusinesses();
      setBusinesses(res.data || []);
    } catch (err: any) { setError(err.message || 'Failed to load businesses'); }
    finally { setLoading(false); }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getAdminBusinessRequests();
      setRequests(res.data || []);
    } catch (err: any) { setError(err.message || 'Failed to load requests'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'users')      fetchUsers();
    if (activeTab === 'businesses') fetchBusinesses();
    if (activeTab === 'requests')   fetchRequests();
    if (activeTab === 'notifications') fetchBusinesses(); // need businesses list for target selector
  }, [activeTab]);

  // ── Handlers that DO NOT need password ──────────────────────────────────
  const handleToggleUserBlock = async (userId: string) => {
    if (!window.confirm('Toggle block status for this user?')) return;
    try {
      await apiService.toggleUserBlock(userId);
      setSuccess('User block status updated');
      fetchUsers();
    } catch (err: any) { setError(err.message); }
  };

  const handleUpdatePlan = async (userId: string, currentPlan: string) => {
    const newPlan = currentPlan === 'pro' ? 'basic' : 'pro';
    const label   = newPlan === 'pro' ? 'nâng cấp lên Pro' : 'hạ xuống Basic';
    if (!window.confirm(`Bạn có muốn ${label} cho tài khoản này không?`)) return;
    try {
      await apiService.updateUserPlan(userId, newPlan);
      setSuccess(`Đã ${label} thành công.`);
      fetchUsers();
    } catch (err: any) { setError(err.message); }
  };

  const handleToggleBusinessBlock = async (businessId: string) => {
    if (!window.confirm('Toggle active status for this business?')) return;
    try {
      await apiService.toggleBusinessBlock(businessId);
      setSuccess('Business status updated');
      fetchBusinesses();
    } catch (err: any) { setError(err.message); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await apiService.resetUserPassword(selectedUser._id, newPassword);
      setSuccess(`Password reset for ${selectedUser.name || selectedUser.email}`);
      setResetModalOpen(false);
      setNewPassword('');
    } catch (err: any) { setError(err.message); }
  };

  const handleUpdateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      await apiService.updateBusinessSubscription(selectedBusiness.id || selectedBusiness._id, newExpiryDate || null);
      setSuccess(`Subscription updated for ${selectedBusiness.name}`);
      setSubModalOpen(false);
      fetchBusinesses();
    } catch (err: any) { setError(err.message); }
  };

  const handleRejectRequest = async (requestId: string) => {
    if (!window.confirm('Từ chối yêu cầu này?')) return;
    try {
      const res = await fetch(`/api/business-requests/${requestId}/reject`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await res.json();
      if (data.success) { setSuccess('Đã từ chối yêu cầu.'); fetchRequests(); }
      else setError(data.message);
    } catch (err: any) { setError(err.message); }
  };

  // ── Handlers that REQUIRE password — open modal ──────────────────────────
  const askPassword = (action: PendingAction) => setPendingAction(action);

  // ── Execute after password confirmed ────────────────────────────────────
  const executeAction = async (password: string) => {
    if (!pendingAction) return;

    switch (pendingAction.type) {
      case 'deleteUser':
        await apiService.deleteUser(pendingAction.userId, password);
        setSuccess('Tài khoản đã được xóa thành công.');
        fetchUsers();
        break;

      case 'deleteBusiness':
        await apiService.adminDeleteBusiness(pendingAction.businessId, password);
        setSuccess('Business đã được xóa vĩnh viễn.');
        fetchBusinesses();
        break;

      case 'sendCode':
        await apiService.adminSendVerifyCode(pendingAction.requestId, password);
        setSuccess('Đã gửi mã xác nhận thành công.');
        fetchRequests();
        break;

      case 'deleteRequest':
        await apiService.deleteAdminBusinessRequest(pendingAction.requestId, password);
        setSuccess('Đã xóa yêu cầu.');
        fetchRequests();
        break;

      case 'clearRequests':
        await apiService.clearAdminBusinessRequests(password, pendingAction.deleteAll);
        setSuccess('Đã xóa lịch sử business requests.');
        fetchRequests();
        break;

      case 'sendNotification':
        const res = await apiService.sendGlobalNotification(
          pendingAction.title,
          pendingAction.message,
          pendingAction.targetBusinessId,
          pendingAction.targetRole,
          password
        );
        setSuccess(`Notification sent to ${res.details?.usersNotifiedDb ?? '?'} users`);
        setNotifyTitle('');
        setNotifyMessage('');
        setTargetBusinessId('');
        setTargetRole('all');
        break;
    }

    setPendingAction(null);
  };

  const handleSendNotification = (e: React.FormEvent) => {
    e.preventDefault();
    askPassword({
      type: 'sendNotification',
      title: notifyTitle,
      message: notifyMessage,
      targetBusinessId: targetBusinessId || undefined,
      targetRole,
    });
  };

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (user?.systemRole !== 'system_admin') {
    return <div className="admin-container error">Access Denied. System Administrators only.</div>;
  }

  const pendingActionTitles: Record<string, string> = {
    deleteUser:       '⚠️ Xóa tài khoản người dùng',
    deleteBusiness:   '⚠️ Xóa Business vĩnh viễn',
    sendCode:         '📨 Gửi mã xác nhận',
    deleteRequest:    '🗑️ Xóa yêu cầu',
    clearRequests:    '🗑️ Xóa lịch sử Requests',
    sendNotification: '📢 Gửi thông báo toàn hệ thống',
  };

  const pendingActionDescs: Partial<Record<string, string>> = {
    deleteUser:       'Thao tác này sẽ xóa vĩnh viễn tài khoản. Không thể hoàn tác!',
    deleteBusiness:   'Thao tác này sẽ xóa toàn bộ dữ liệu Business. Không thể hoàn tác!',
    clearRequests:    'Sẽ xóa toàn bộ lịch sử đã hoàn thành/từ chối.',
    sendNotification: 'Sẽ gửi push notification tới toàn bộ người dùng được chọn.',
  };

  return (
    <div className="admin-container">
      <h1 className="admin-title">System Admin Dashboard</h1>

      {error   && <div className="admin-alert error">  {error}   <button onClick={() => setError('')}>×</button></div>}
      {success && <div className="admin-alert success">{success} <button onClick={() => setSuccess('')}>×</button></div>}

      <div className="admin-tabs">
        <button className={activeTab === 'users'         ? 'active' : ''} onClick={() => setActiveTab('users')}>Users</button>
        <button className={activeTab === 'businesses'    ? 'active' : ''} onClick={() => setActiveTab('businesses')}>Businesses</button>
        <button className={activeTab === 'requests'      ? 'active' : ''} onClick={() => setActiveTab('requests')}>Business Requests</button>
        <button className={activeTab === 'notifications' ? 'active' : ''} onClick={() => setActiveTab('notifications')}>Global Notification</button>
      </div>

      <div className="admin-content">
        {loading ? <p>Loading data...</p> : (
          <>
            {/* ── USERS TAB ── */}
            {activeTab === 'users' && (
              <div className="table-responsive">
                <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap'}}>
                  <span style={{fontWeight:600, fontSize:'0.95rem'}}>
                    Tổng: {users.length} người dùng
                  </span>
                  <button
                    className={`btn ${noBizFilter ? 'btn-warning' : 'btn-outline'}`}
                    style={{fontSize:'0.82rem', padding:'4px 12px'}}
                    onClick={() => setNoBizFilter(v => !v)}
                  >
                    {noBizFilter ? '✖ Bỏ lọc' : '🔍 Chỉ hiện chưa có Business'}
                    {!noBizFilter && (
                      <span style={{marginLeft:6, background:'#ef4444', color:'#fff', borderRadius:10, padding:'1px 7px', fontSize:'0.75rem'}}>
                        {users.filter(u => !u.businesses || u.businesses.length === 0).length}
                      </span>
                    )}
                  </button>
                </div>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Username</th>
                      <th>Business</th>
                      <th>System Role</th><th>Plan</th><th>Status</th>
                      <th>Last IP Address</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(u => !noBizFilter || !u.businesses || u.businesses.length === 0)
                      .map(u => {
                        const hasNoBiz = !u.businesses || u.businesses.length === 0;
                        return (
                          <tr key={u._id} className={u.isBlocked ? 'blocked-row' : ''}>
                            <td>{u.name || '-'}</td>
                            <td>
                              {u.email}
                              {hasNoBiz && (
                                <div style={{marginTop:3}}>
                                  <span style={{
                                    background:'#fef3c7', color:'#92400e',
                                    borderRadius:10, padding:'1px 8px',
                                    fontSize:'0.72rem', fontWeight:600
                                  }}>⚠️ Chưa có Business</span>
                                </div>
                              )}
                            </td>
                            <td>{u.username || '-'}</td>
                            <td style={{textAlign:'center'}}>
                              {hasNoBiz ? (
                                <span style={{color:'#ef4444', fontWeight:600}}>—</span>
                              ) : (
                                <span style={{color:'#10b981', fontWeight:600}}>{u.businesses.length}</span>
                              )}
                            </td>
                            <td><span className={`badge ${u.systemRole === 'system_admin' ? 'admin' : 'user'}`}>{u.systemRole || 'user'}</span></td>
                            <td><span className={`badge ${(u.plan||'basic')==='pro' ? 'pro-badge' : 'basic-badge'}`}>{(u.plan||'basic').toUpperCase()}</span></td>
                            <td>{u.isBlocked ? <span className="status blocked">Blocked</span> : <span className="status active">Active</span>}</td>
                            <td>{u.lastIpAddress || 'Never logged in'}</td>
                            <td className="actions-cell">
                              {u.systemRole !== 'system_admin' && (
                                <>
                                  {hasNoBiz && (
                                    <a
                                      href={`mailto:${u.email}?subject=Tham%20gia%20Business%20tr%C3%AAn%20Phanh&body=Xin%20ch%C3%A0o%2C%0A%0ABạn%20đã%20đăng%20ký%20tài%20khoản%20Phanh%20thành%20công.%0AVui%20lòng%20liên%20hệ%20admin%20để%20tạo%20hoặc%20tham%20gia%20Business.%0A%0ATrân%20trọng`}
                                      className="btn btn-primary"
                                      style={{textDecoration:'none', display:'inline-block'}}
                                    >
                                      📧 Email
                                    </a>
                                  )}
                                  <button className={`btn ${u.isBlocked ? 'btn-success' : 'btn-danger'}`} onClick={() => handleToggleUserBlock(u._id)}>
                                    {u.isBlocked ? 'Unblock' : 'Block'}
                                  </button>
                                  <button className="btn btn-warning" onClick={() => { setSelectedUser(u); setResetModalOpen(true); }}>
                                    Reset Pwd
                                  </button>
                                  <button className={`btn ${u.plan === 'pro' ? 'btn-secondary' : 'btn-primary'}`} onClick={() => handleUpdatePlan(u._id, u.plan || 'basic')}>
                                    {u.plan === 'pro' ? '⬇ Basic' : '⬆ Pro'}
                                  </button>
                                  <button className="btn btn-danger" onClick={() => askPassword({ type: 'deleteUser', userId: u._id })}>
                                    🗑️ Xóa
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── BUSINESSES TAB ── */}
            {activeTab === 'businesses' && (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Business Name</th><th>Owner Email</th>
                      <th>Service Started</th><th>Expiry Date</th>
                      <th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.map(b => (
                      <tr key={b.id || b._id} className={!b.active ? 'blocked-row' : ''}>
                        <td>{b.name}<br/><small className="text-muted">Code: {b.bookingCode}</small></td>
                        <td>{b.ownerId?.email || '-'}</td>
                        <td>{new Date(b.createdAt).toLocaleDateString()}</td>
                        <td>
                          {b.subscriptionExpiry ? new Date(b.subscriptionExpiry).toLocaleDateString() : 'Never'}
                          {b.subscriptionExpiry && new Date(b.subscriptionExpiry) < new Date() &&
                            <span style={{color:'red',marginLeft:'5px',fontSize:'0.8em'}}>(Expired)</span>}
                        </td>
                        <td>
                          {b.active ? <span className="status active">Active</span> : (
                            <div>
                              <span className="status blocked">Blocked</span>
                              {b.blockedAt && <div style={{fontSize:'0.8em',color:'red',marginTop:'4px'}}>
                                ({Math.floor((Date.now() - new Date(b.blockedAt).getTime()) / 86400000)} ngày)
                              </div>}
                            </div>
                          )}
                        </td>
                        <td className="actions-cell">
                          <button className="btn btn-warning" style={{marginRight:5}} onClick={() => {
                            setSelectedBusiness(b);
                            setNewExpiryDate(b.subscriptionExpiry ? new Date(b.subscriptionExpiry).toISOString().slice(0,10) : '');
                            setSubModalOpen(true);
                          }}>Extend</button>
                          <button className={`btn ${b.active ? 'btn-danger' : 'btn-success'}`} style={{marginRight:5}} onClick={() => handleToggleBusinessBlock(b.id || b._id)}>
                            {b.active ? 'Block' : 'Unblock'}
                          </button>
                          <button className="btn btn-danger" onClick={() => askPassword({ type: 'deleteBusiness', businessId: b.id || b._id })}>
                            🗑️ Xóa
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── BUSINESS REQUESTS TAB ── */}
            {activeTab === 'requests' && (
              <div>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8}}>
                  <h2 style={{margin:0, fontSize:'1.1rem'}}>Lịch sử Business Requests ({requests.length})</h2>
                  <div style={{display:'flex', gap:8}}>
                    <button className="btn btn-warning" onClick={() => askPassword({ type: 'clearRequests', deleteAll: false })}>
                      🗑️ Xóa lịch sử (Verified/Rejected)
                    </button>
                    <button className="btn btn-danger" onClick={() => askPassword({ type: 'clearRequests', deleteAll: true })}>
                      🗑️ Xóa tất cả
                    </button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Người dùng</th><th>Tên Business</th><th>SĐT</th>
                        <th>Trạng thái</th><th>Ngày gửi</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.length === 0 ? (
                        <tr><td colSpan={6} style={{textAlign:'center',color:'#64748b',padding:'24px'}}>Không có request nào.</td></tr>
                      ) : requests.map(r => {
                        const statusInfo = STATUS_LABELS[r.status] || { label: r.status, color: '#64748b' };
                        return (
                          <tr key={r._id}>
                            <td>
                              <div>{r.userId?.name || r.contactName || '-'}</div>
                              <small className="text-muted">{r.userId?.email || r.contactEmail || ''}</small>
                            </td>
                            <td>{r.businessName}</td>
                            <td>{r.contactPhone || '-'}</td>
                            <td>
                              <span style={{
                                background: statusInfo.color + '22',
                                color: statusInfo.color,
                                padding: '3px 10px',
                                borderRadius: 20,
                                fontWeight: 600,
                                fontSize: '0.82rem'
                              }}>{statusInfo.label}</span>
                            </td>
                            <td>{new Date(r.createdAt).toLocaleDateString('vi-VN')}</td>
                            <td className="actions-cell">
                              {(r.status === 'pending' || r.status === 'code_sent') && (
                                <>
                                  <button className="btn btn-primary" onClick={() => askPassword({ type: 'sendCode', requestId: r._id })}>
                                    📨 Gửi mã
                                  </button>
                                  <button className="btn btn-danger" onClick={() => handleRejectRequest(r._id)}>
                                    ✖ Từ chối
                                  </button>
                                </>
                              )}
                              <button className="btn btn-warning" onClick={() => askPassword({ type: 'deleteRequest', requestId: r._id })}>
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── NOTIFICATIONS TAB ── */}
            {activeTab === 'notifications' && (
              <div className="notification-form-container">
                <h2>Broadcast Global Notification</h2>
                <p>This will send a push notification to all active users and save it in their notification inbox.</p>
                <form onSubmit={handleSendNotification} className="admin-form">
                  <div className="form-group">
                    <label>Target Business (Optional)</label>
                    <select value={targetBusinessId} onChange={e => setTargetBusinessId(e.target.value)} className="form-input">
                      <option value="">-- All Businesses --</option>
                      {businesses.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </select>
                  </div>
                  {targetBusinessId && (
                    <div className="form-group">
                      <label>Target Role</label>
                      <select value={targetRole} onChange={e => setTargetRole(e.target.value)} className="form-input">
                        <option value="all">All Users in Business</option>
                        <option value="owner">Owners Only</option>
                        <option value="manager">Managers Only</option>
                        <option value="staff">Staff Only</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Title</label>
                    <input type="text" value={notifyTitle} onChange={e => setNotifyTitle(e.target.value)} required placeholder="e.g. System Maintenance" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Message</label>
                    <textarea value={notifyMessage} onChange={e => setNotifyMessage(e.target.value)} required placeholder="Enter the message to broadcast to all users..." rows={5} />
                  </div>
                  <button type="submit" className="btn btn-primary btn-large">📢 Send Broadcast</button>
                </form>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── RESET PASSWORD MODAL ── */}
      {resetModalOpen && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Reset Password for {selectedUser.email}</h3>
            <form onSubmit={handleResetPassword} className="admin-form">
              <div className="form-group">
                <label>New Temporary Password</label>
                <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} placeholder="e.g. Temp123!@#" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setResetModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-warning">Save Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── SUBSCRIPTION MODAL ── */}
      {subModalOpen && selectedBusiness && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Manage Subscription: {selectedBusiness.name}</h3>
            <p style={{marginBottom:15}}>Service Started: {new Date(selectedBusiness.createdAt).toLocaleDateString()}</p>
            <form onSubmit={handleUpdateSubscription} className="admin-form">
              <div className="form-group">
                <label>Subscription Expiry Date</label>
                <input type="date" value={newExpiryDate} onChange={e => setNewExpiryDate(e.target.value)} className="form-input" />
                <small className="text-muted">Leave blank for unlimited access.</small>
              </div>
              <div style={{display:'flex',gap:10,marginTop:10}}>
                <button type="button" className="btn btn-outline" onClick={() => { const d=new Date(); d.setMonth(d.getMonth()+1); setNewExpiryDate(d.toISOString().slice(0,10)); }}>+1 Month</button>
                <button type="button" className="btn btn-outline" onClick={() => { const d=new Date(); d.setFullYear(d.getFullYear()+1); setNewExpiryDate(d.toISOString().slice(0,10)); }}>+1 Year</button>
              </div>
              <div className="modal-actions" style={{marginTop:20}}>
                <button type="button" className="btn" onClick={() => setSubModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 🔐 ADMIN ACTION PASSWORD MODAL */}
      <AdminActionPasswordModal
        isOpen={!!pendingAction}
        title={pendingAction ? pendingActionTitles[pendingAction.type] ?? 'Xác nhận hành động' : ''}
        description={pendingAction ? pendingActionDescs[pendingAction.type] : undefined}
        onConfirm={executeAction}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
